import { PASSKEY_PRF_SALT } from "@timecapsule/crypto";
import type { PasskeyAssertion } from "./api";
import { msg } from "./i18n";

/**
 * Passkey in two roles. As a key-derivation device (PLAN.md 4.6): the WebAuthn prf extension
 * returns HMAC(secret inside the passkey, PASSKEY_PRF_SALT), 32 bytes, the same every time for the
 * same passkey; for that, challenges are random and nobody checks the signature. As a sign-in
 * (PLAN.md 4.14): the server stores the passkey's public key at enrolment and checks the signature
 * over its own challenge (signInAssertion). The PRF output never leaves the browser.
 */

export class PasskeyError extends Error {
  constructor(
    readonly code: "UNSUPPORTED" | "CANCELLED" | "ABORTED" | "NO_PRF" | "FAILED",
    message: string,
  ) {
    super(message);
    this.name = "PasskeyError";
  }
}

type PrfOutputs = { enabled?: boolean; results?: { first?: ArrayBuffer } };

const buffer = (bytes: Uint8Array): ArrayBuffer => bytes.slice().buffer as ArrayBuffer;
const random = (n: number) => buffer(crypto.getRandomValues(new Uint8Array(n)));

export function toBase64url(bytes: ArrayBuffer): string {
  let s = "";
  for (const b of new Uint8Array(bytes)) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64url(text: string): ArrayBuffer {
  const s = atob(text.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((text.length + 3) % 4));
  return buffer(Uint8Array.from(s, (c) => c.charCodeAt(0)));
}

const prfInput = { prf: { eval: { first: buffer(PASSKEY_PRF_SALT) } } } as AuthenticationExtensionsClientInputs;

export function passkeysAvailable(): boolean {
  return typeof window !== "undefined" && !!window.PublicKeyCredential && !!navigator.credentials?.create;
}

/** How long the browser waits for the passkey window (owner's finding, 4b step 1: a prompt that never showed waited 120 s). */
export const PASSKEY_TIMEOUT_MS = 60_000;


/** The member pressed Cancel while the page waited for the passkey: not an error to show. */
export const isAborted = (err: unknown) => err instanceof PasskeyError && err.code === "ABORTED";

function explain(err: unknown): PasskeyError {
  if (err instanceof PasskeyError) return err;
  const name = err instanceof DOMException ? err.name : "";
  if (name === "AbortError") return new PasskeyError("ABORTED", "Cancelled by the member.");
  if (name === "NotAllowedError") return new PasskeyError("CANCELLED", `${msg().errors.passkeyClosed} ${msg().common.noWindowHint}`);
  return new PasskeyError("FAILED", msg().errors.passkeyFailed(err instanceof Error ? err.message : String(err)));
}

/**
 * Creates a passkey for this site and returns its id and, when the authenticator supports it,
 * the PRF output. Some authenticators only report `prf.enabled` at creation; then one assertion
 * fetches the output. `prf` is null when the passkey has no PRF.
 */
export type CreatedPasskey = {
  credentialId: string;
  prf: Uint8Array | null;
  /** For sign-in enrolment: the public key (base64url SubjectPublicKeyInfo) and its COSE algorithm; null where the browser does not tell. */
  publicKey: string | null;
  alg: number | null;
};

export async function createPasskey(user: { name: string; displayName: string }, signal?: AbortSignal): Promise<CreatedPasskey> {
  if (!passkeysAvailable()) throw new PasskeyError("UNSUPPORTED", msg().errors.passkeyUnsupported);
  let cred: PublicKeyCredential;
  try {
    const created = await navigator.credentials.create({
      publicKey: {
        rp: { name: "Vrelis", id: location.hostname },
        // Random per passkey: a fixed handle would let a password manager replace an older passkey
        // that still locks a key copy on another device.
        user: { id: random(16), name: user.name, displayName: user.displayName },
        challenge: random(32),
        pubKeyCredParams: [
          { type: "public-key", alg: -7 },
          { type: "public-key", alg: -257 },
        ],
        authenticatorSelection: { residentKey: "required", requireResidentKey: true, userVerification: "required" },
        timeout: PASSKEY_TIMEOUT_MS,
        extensions: prfInput,
      },
      signal,
    });
    if (!created) throw new PasskeyError("CANCELLED", msg().errors.noPasskeyCreated);
    cred = created as PublicKeyCredential;
  } catch (err) {
    throw explain(err);
  }
  const credentialId = toBase64url(cred.rawId);
  const response = cred.response as AuthenticatorAttestationResponse;
  const spki = typeof response.getPublicKey === "function" ? response.getPublicKey() : null;
  const signIn = {
    publicKey: spki ? toBase64url(spki) : null,
    alg: typeof response.getPublicKeyAlgorithm === "function" ? response.getPublicKeyAlgorithm() : null,
  };
  const ext = (cred.getClientExtensionResults() as { prf?: PrfOutputs }).prf;
  if (ext?.results?.first) return { credentialId, prf: new Uint8Array(ext.results.first), ...signIn };
  if (ext?.enabled === false) return { credentialId, prf: null, ...signIn };
  try {
    return { credentialId, prf: await passkeyPrf(credentialId, signal), ...signIn };
  } catch (err) {
    if (err instanceof PasskeyError && err.code === "NO_PRF") return { credentialId, prf: null, ...signIn };
    throw err;
  }
}

/**
 * Signs the server's challenge with any passkey of this site the member picks (discoverable: no
 * address needed). Only passkeys enrolled for sign-in are accepted by the server.
 */
export async function signInAssertion(challenge: string, signal?: AbortSignal): Promise<PasskeyAssertion> {
  if (!passkeysAvailable()) throw new PasskeyError("UNSUPPORTED", msg().errors.passkeyUnusable);
  let cred: PublicKeyCredential;
  try {
    const got = await navigator.credentials.get({
      publicKey: { challenge: fromBase64url(challenge), rpId: location.hostname, userVerification: "required", timeout: PASSKEY_TIMEOUT_MS },
      signal,
    });
    if (!got) throw new PasskeyError("CANCELLED", msg().errors.noPasskeyUsed);
    cred = got as PublicKeyCredential;
  } catch (err) {
    throw explain(err);
  }
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    credential_id: toBase64url(cred.rawId),
    client_data_json: toBase64url(r.clientDataJSON),
    authenticator_data: toBase64url(r.authenticatorData),
    signature: toBase64url(r.signature),
  };
}

/** Asks the passkey with this id (synced or local) for its PRF output. Shows the system passkey prompt. */
export async function passkeyPrf(credentialId: string, signal?: AbortSignal): Promise<Uint8Array> {
  if (!passkeysAvailable()) throw new PasskeyError("UNSUPPORTED", msg().errors.passkeyUnusable);
  let cred: PublicKeyCredential;
  try {
    const got = await navigator.credentials.get({
      publicKey: {
        challenge: random(32),
        rpId: location.hostname,
        allowCredentials: [{ type: "public-key", id: fromBase64url(credentialId) }],
        userVerification: "required",
        timeout: PASSKEY_TIMEOUT_MS,
        extensions: prfInput,
      },
      signal,
    });
    if (!got) throw new PasskeyError("CANCELLED", msg().errors.noPasskeyUsed);
    cred = got as PublicKeyCredential;
  } catch (err) {
    throw explain(err);
  }
  const first = (cred.getClientExtensionResults() as { prf?: PrfOutputs }).prf?.results?.first;
  if (!first) throw new PasskeyError("NO_PRF", msg().errors.noPrf);
  return new Uint8Array(first);
}
